import type {
  ITherapyDiaryAssignment,
  ITherapyDiaryEntry,
  ITherapyDiaryItemInput,
  ITherapyDiaryItemSource,
  TherapyDiaryToolType,
} from '../../../services/therapy-diary.api';
import type { IJournalResult } from '../../../store/types';

import { HOMEWORK_ASSIGNMENT_ID } from './templates';

/** Максимум элементов в одной записи дневника — ограничение API. */
export const DIARY_MAX_ITEMS = 20;
/** Лимиты длины текста задания — из схемы API. */
export const DIARY_ITEM_TEXT_MAX = 10000;
export const DIARY_GENERAL_TEXT_MAX = 20000;

const TOOL_TYPES: readonly TherapyDiaryToolType[] = [
  'emotions',
  'mac_cards',
  'wheel',
  'color_code',
  'clouds',
  'sand_therapy',
  'dpdg',
];

/** dpdg — обычный текст задания без привязки к срабатыванию инструмента: source ему не нужен. */
const DPDG_TOOL_TYPE: TherapyDiaryToolType = 'dpdg';

export interface DiaryPayload {
  items: ITherapyDiaryItemInput[];
  general_assignment: ITherapyDiaryAssignment | null;
}

export interface DiarySelectionState {
  /** Порядок регистрации секций на вкладке — он же порядок items. */
  tools: { id: string; title: string }[];
  includedIds: string[];
  tasks: Record<string, string>;
}

export interface BuildDiaryPayloadInput extends DiarySelectionState {
  meetData: IJournalResult | null;
}

/**
 * Единственное место, где текст задания готовится для API.
 * В свагере assignment описан как object, но сервер принимает строку — менять здесь и в fromAssignment.
 */
export const toAssignment = (text: string): ITherapyDiaryAssignment | null => text.trim() || null;

/**
 * Обратное преобразование для восстановления черновика.
 * В свагере assignment описан как object без полей, поэтому кроме строки разбираем
 * и объект с текстом: иначе сохранённое задание молча терялось бы и уходило как null.
 */
export const fromAssignment = (assignment: unknown): string => {
  if (typeof assignment === 'string') return assignment;
  if (typeof assignment === 'object' && assignment !== null) {
    const { text, value } = assignment as { text?: unknown; value?: unknown };
    if (typeof text === 'string') return text;
    if (typeof value === 'string') return value;
  }
  return '';
};

const isToolType = (id: string): id is TherapyDiaryToolType => (TOOL_TYPES as readonly string[]).includes(id);

/** Последнее по порядку срабатывание инструмента — его и получает клиент. */
const lastOccurrence = (
  meetData: IJournalResult | null,
  toolType: TherapyDiaryToolType,
): ITherapyDiaryItemSource | null => {
  const occurrences = meetData?.[toolType];
  if (!Array.isArray(occurrences) || occurrences.length === 0) return null;

  const occurrenceIndex = occurrences.length - 1;
  const dateTime = occurrences[occurrenceIndex]?.date_time;
  // Без date_time сервер ответит 422, а починить это из UI нельзя — инструмент проще пропустить.
  if (!dateTime) return null;

  return { date_time: dateTime, occurrence_index: occurrenceIndex };
};

/** Состояние вкладки → тело запроса. */
export const buildDiaryPayload = ({ tools, includedIds, tasks, meetData }: BuildDiaryPayloadInput): DiaryPayload => {
  const items: ITherapyDiaryItemInput[] = [];

  for (const tool of tools) {
    if (!includedIds.includes(tool.id)) continue;
    // Домашнее задание — отдельное поле записи, а не элемент items.
    if (tool.id === HOMEWORK_ASSIGNMENT_ID) continue;
    if (!isToolType(tool.id)) continue;

    if (tool.id === DPDG_TOOL_TYPE) {
      items.push({ tool_type: tool.id, assignment: toAssignment(tasks[tool.id] ?? '') });
      continue;
    }

    const source = lastOccurrence(meetData, tool.id);
    if (!source) continue;

    items.push({ tool_type: tool.id, source, assignment: toAssignment(tasks[tool.id] ?? '') });
  }

  const generalAssignment = includedIds.includes(HOMEWORK_ASSIGNMENT_ID)
    ? toAssignment(tasks[HOMEWORK_ASSIGNMENT_ID] ?? '')
    : null;

  return { items: items.slice(0, DIARY_MAX_ITEMS), general_assignment: generalAssignment };
};

export const isEmptyPayload = (payload: DiaryPayload): boolean =>
  payload.items.length === 0 && payload.general_assignment === null;

/** Сохранённая запись → состояние вкладки: инструмент выбран, если он есть в items. */
export const entryToSelection = (entry: ITherapyDiaryEntry): Pick<DiarySelectionState, 'includedIds' | 'tasks'> => {
  const includedIds: string[] = [];
  const tasks: Record<string, string> = {};

  for (const item of entry.items) {
    includedIds.push(item.tool_type);
    tasks[item.tool_type] = fromAssignment(item.assignment);
  }

  if (entry.general_assignment) {
    includedIds.push(HOMEWORK_ASSIGNMENT_ID);
    tasks[HOMEWORK_ASSIGNMENT_ID] = fromAssignment(entry.general_assignment);
  }

  return { includedIds, tasks };
};
